import axios from "axios";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:3000";

// Configure axios defaults
axios.defaults.withCredentials = true;
axios.defaults.baseURL = backendUrl;

// Request interceptor - no need to add auth token since we're using cookies
axios.interceptors.request.use(
  (config) => {
    // Cookies are automatically sent with withCredentials: true
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API service functions
export const apiService = {
  // Auth endpoints
  auth: {
    login: (credentials) => axios.post('/api/auth/login', credentials),
    register: (userData) => axios.post('/api/auth/register', userData),
    logout: () => axios.post('/api/auth/logout'),
    isAuth: () => axios.get('/api/auth/is-auth'),
    forgotPassword: (email) => axios.post('/api/auth/forgot-password', { email }),
    resetPassword: (token, password) => axios.post('/api/auth/reset-password', { token, password }),
  },

  // User endpoints
  user: {
    getData: () => axios.get('/api/user/data'),
    getProfile: () => axios.get('/api/user/profile'),
    updateProfile: (data) => axios.put('/api/user/profile', data),
    changePassword: (data) => axios.put('/api/user/change-password', data),
    uploadAvatar: (file) => {
      const formData = new FormData();
      formData.append('avatar', file);
      return axios.post('/api/user/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
  },

  // Course endpoints
  courses: {
    getAll: (params) => axios.get('/api/course', { params }),
    getById: (id) => axios.get(`/api/course/${id}`),
    create: (data) => axios.post('/api/course', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    update: (id, data) => axios.put(`/api/course/${id}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    delete: (id) => axios.delete(`/api/course/${id}`),
    getBySlug: (slug) => axios.get(`/api/course/slug/${slug}`),
    getAllPublic: () => axios.get('/api/course/all'),
    getCreated: () => axios.get('/api/course/my-courses'),
    togglePublish: (id, published) => axios.patch(`/api/course/${id}/publish`, { published }),
  },

  // Lesson endpoints
  lessons: {
    getByCourse: (courseId) => axios.get(`/api/lessions/${courseId}`),
    create: (courseId, data) => axios.post(`/api/lessions/${courseId}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    update: (lessonId, data) => axios.put(`/api/lessions/lession/${lessonId}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    delete: (lessonId) => axios.delete(`/api/lessions/lession/${lessonId}`),
    getById: (lessonId) => axios.get(`/api/lessions/lession/${lessonId}`),
    markComplete: (lessonId) => axios.post(`/api/lessons/${lessonId}/complete`),
  },

  // Assignment endpoints
  assignments: {
    getByCourse: (courseId) => axios.get(`/api/courses/${courseId}/assignments`),
    create: (courseId, data) => axios.post(`/api/courses/${courseId}/assignments`, data),
    update: (assignmentId, data) => axios.put(`/api/assignments/${assignmentId}`, data),
    delete: (assignmentId) => axios.delete(`/api/assignments/${assignmentId}`),
    submit: (assignmentId, data) => axios.post(`/api/assignments/${assignmentId}/submit`, data),
    getSubmissions: (assignmentId) => axios.get(`/api/assignments/${assignmentId}/submissions`),
    grade: (submissionId, data) => axios.put(`/api/assignments/submissions/${submissionId}/grade`, data),
  },

  // Enrollment endpoints
  enrollment: {
    enroll: (courseId) => axios.post(`/api/enroll/${courseId}`),
    checkEnrollment: (courseId) => axios.get(`/api/enroll/check/${courseId}`),
    getEnrolledCourses: () => axios.get('/api/enroll/my-courses'),
    getStudentsInCourse: (courseId) => axios.get(`/api/enroll/${courseId}/students`),
    updateProgress: (enrollmentId, progress) => axios.put(`/api/enroll/${enrollmentId}/progress`, { progress }),
    getProgress: (courseId) => axios.get(`/api/enroll/${courseId}/progress`),
  },

  // Progress endpoints
  progress: {
    markLessonComplete: (courseId, lessonId) => axios.post(`/api/progress/${courseId}/${lessonId}/complete`),
    markLessonIncomplete: (courseId, lessonId) => axios.post(`/api/progress/${courseId}/${lessonId}/incomplete`),
    getCompletedLessons: (courseId) => axios.get(`/api/progress/${courseId}/completed-lessions`),
    previewFreeLesson: (lessonId) => axios.get(`/api/progress/preview/${lessonId}`),
  },

  // Notification endpoints
  notifications: {
    getAll: () => axios.get('/api/notifications'),
    markAsRead: (id) => axios.put(`/api/notifications/${id}/read`),
    markAllAsRead: () => axios.put('/api/notifications/read-all'),
    delete: (id) => axios.delete(`/api/notifications/${id}`),
  },
};

export default apiService;
